<script lang="ts" setup>
import { normalizeClasses } from '#imports';

const { desktop } = useMedia();

const props = withDefaults(defineProps<{
    usePanels?: boolean;
    scroll?: boolean;
    leftPanelName?: string;
    rightPanelName?: string;
    hideLeftButton?: boolean;
    hideRightButton?: boolean;
    openLeft?: boolean;
    openRight?: boolean;
    disableLeft?: boolean;
    disableRight?: boolean;
    disableHeader?: boolean;
    disableFooter?: boolean;
    disableToolbar?: boolean;
    overflow?: boolean;
    stickyFooter?: boolean;
    stickyPanelContent?: boolean;
    leftClass?: string;
    rightClass?: string;
    bodyClass?: string;
    height?: 'fixed' | 'auto';
    subcard?: boolean;
}>(), {
    usePanels: false,
    scroll: false,
    leftPanelName: '[[[Lewy panel]]]',
    rightPanelName: '[[[Prawy panel]]]',
    hideLeftButton: false,
    hideRightButton: false,
    openLeft: false,
    openRight: false,
    disableLeft: false,
    disableRight: false,
    disableHeader: false,
    disableFooter: false,
    disableToolbar: false,
    overflow: false,
    stickyFooter: true,
    stickyPanelContent: false,
    leftClass: 'p-3',
    rightClass: 'p-3',
    bodyClass: 'p-3',
    height: 'auto',
    subcard: false
});
const emit = defineEmits<{
    (e: 'show-left'): void;
    (e: 'hide-left'): void;
    (e: 'show-right'): void;
    (e: 'hide-right'): void;
}>();

const slots = useSlots() as any;

const hasLeftSlot = computed(() => slots.left && !props.disableLeft);
const showLeft = ref(false);
const showLeftButton = computed(() => props.usePanels && hasLeftSlot.value && !props.hideLeftButton);
const toggleLeft = (value: boolean): void =>
{
    showLeft.value = value;

    if (value == true)
    {
        showRight.value = false;
    }
};

const hasRightSlot = computed(() => slots.right && !props.disableRight);
const showRight = ref(false);
const showRightButton = computed(() => props.usePanels && hasRightSlot.value && !props.hideRightButton);
const toggleRight = (value: boolean): void =>
{
    showRight.value = value;

    if (value == true)
    {
        showLeft.value = false;
    }
};

const hasHeader = computed(() => (slots.header || slots.modal) && !props.disableHeader);
const hasFooter = computed(() => slots.footer && !props.disableFooter);
const hasToobar = computed(() => slots.toolbar && !props.disableToolbar);

const scrollData = ref<any>({});
const wrapperRef = ref(null);
const toolbarRef = ref(null);
const cardBodyRef = ref(null);
const panelRightRef = ref(null);
const panelRightContentRef = ref(null);

const ob = ref(new ResizeObserver(() =>
{
    if (offset.value + panelRightContentRef.value.offsetHeight >= panelRightRef.value.offsetHeight)
    {
        offset.value = panelRightRef.value.offsetHeight - panelRightContentRef.value.offsetHeight - 32;
    }
    else
    {
        adjustOffset();
    }
}));

/* The function is adapted to filemanager - it may not work properly in other places */
const dynamicScroll = computed(() =>
{
    return props.stickyPanelContent && desktop() && /Mobile|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) === false;
});

onMounted(() =>
{
    if (dynamicScroll.value)
    {
        wrapperRef.value = document.querySelector('[data-name="wrapper"]');
        scrollData.value = useScroll(wrapperRef.value, { behavior: 'smooth' });
    }
});

const delta = ref(0);

useResizeObserver(cardBodyRef, (entries) =>
{
    if (props.height == 'fixed')
    {
        if (delta.value == 0)
        {
            const { height } = entries[0].contentRect;

            delta.value = window.innerHeight - height;
        }

        cardBodyRef.value.style.height = `calc(100dvh - ${delta.value}px)`;
    }
});

onBeforeUnmount(() =>
{
    if (panelRightContentRef.value)
        ob.value.unobserve(panelRightContentRef.value);
});

watch(panelRightContentRef, (value, old) =>
{
    if (dynamicScroll.value && (value || old))
    {
        if (value)
            ob.value.observe(value);
        else
            ob.value.unobserve(old);
    }
});

const offset = ref(0);

const handleScrollDown = (y: number, diff: number, maxOffset: number): void =>
{
    if (scrollData.value.arrivedState.bottom || offset.value >= maxOffset)
        offset.value = maxOffset;
    else if (y > diff + offset.value)
        offset.value = y - diff;
};

const handleScrollUp = (y: number): void =>
{
    const panelTop = panelRightRef.value.offsetTop;

    if (y - panelTop <= 0)
        offset.value = 0;
    else if (y - panelTop < offset.value)
        offset.value = y - panelTop;
};

const handleScroll = (y: number, viewport: number, panelHeight: number): void =>
{
    const diff = panelHeight - viewport + 32;
    const maxOffset = panelRightRef.value.offsetHeight - panelHeight - 32;

    const { bottom: scrollingDown, top: scrollingUp } = scrollData.value.directions ?? {};

    if (scrollingDown || y - diff > offset.value)
        handleScrollDown(y, diff, maxOffset);
    else if (scrollingUp)
        handleScrollUp(y);
};

const adjustOffsetForNonScrollablePanel = (y: number): void =>
{
    const panelTop = panelRightRef.value.offsetTop;

    if (y > panelTop)
        offset.value = y - panelTop;
    else
        offset.value = 0;
};

const adjustOffset = (): void =>
{
    if (!panelRightRef.value) return;

    const y = parseFloat(scrollData.value.y?.toFixed(2));

    const height = 65 +
        wrapperRef.value.offsetTop +
        toolbarRef.value?.offsetHeight || 0 +
        cardBodyRef.value?.offsetTop || 0;

    const viewport = window.innerHeight - height;
    const panelHeight = panelRightContentRef.value.offsetHeight;

    if (panelHeight > viewport)
        handleScroll(y, viewport, panelHeight);
    else
        adjustOffsetForNonScrollablePanel(y);
};

watch(scrollData, () =>
{
    if (dynamicScroll.value)
        adjustOffset();
}, { deep: true });

watch(showLeft, (value) =>
{
    if (value)
        emit('show-left');
    else
        emit('hide-left');
});

watch(showRight, (value) =>
{
    if (value)
        emit('show-right');
    else
        emit('hide-right');
});

watch(() => props.openLeft, (value) =>
{
    toggleLeft(value);
},
{ immediate: true });

watch(() => props.openRight, (value) =>
{
    toggleRight(value);
},
{ immediate: true });
</script>

<template>
    <div ref="cardRef" class="flex-fill d-flex flex-column">
        <div v-if="hasToobar" ref="toolbarRef" class="toolbar sticky-top py-2 px-3 mt-n3 mx-n3 bg-body-tertiary" :class="{ 'subcard': subcard }">
            <slot name="toolbar" />
        </div>
        <div class="card card-form flex-fill" :class="{ 'mobile': mobile, 'tablet': tablet, 'phone': phone, 'responsive': mobile && usePanels, 'overflow-hidden': overflow }">
            <div v-if="hasHeader || (mobile && (showLeftButton || showRightButton))" class="card-header">
                <div class="d-flex flex-nowrap align-items-center">
                    <div v-if="mobile && showLeftButton" class="pe-3">
                        <button v-if="!showLeft" class="btn btn-light" @click.prevent="toggleLeft(true)">
                            <span v-if="!phone" class="me-1">{{ $t(leftPanelName) }}</span>
                            <i class="fas fa-chevron-right" />
                        </button>
                        <button v-if="showLeft" class="btn btn-light" @click.prevent="toggleLeft(false)">
                            <i class="fas fa-chevron-left" />
                            <span v-if="!phone" class="ms-1">{{ $t(leftPanelName) }}</span>
                        </button>
                    </div>
                    <div class="flex-fill">
                        <slot name="header" />
                    </div>
                    <div v-if="mobile && showRightButton" class="ps-3">
                        <button v-if="!showRight" class="btn btn-light" @click.prevent="toggleRight(true)">
                            <i class="fas fa-chevron-left" />
                            <span v-if="!phone" class="ms-1">{{ $t(rightPanelName) }}</span>
                        </button>
                        <button v-if="showRight" class="btn btn-light" @click.prevent="toggleRight(false)">
                            <span v-if="!phone" class="me-1">{{ $t(rightPanelName) }}</span>
                            <i class="fas fa-chevron-right" />
                        </button>
                    </div>
                    <div v-if="slots.modal" class="ps-3">
                        <slot name="modal" />
                    </div>
                </div>
            </div>
            <div ref="cardBodyRef" class="card-body d-flex p-0" :class="{ 'flex-column': mobile && !usePanels, 'scroll': scroll }">
                <div v-if="hasLeftSlot" class="panel left d-flex flex-column" :class="{ 'show': showLeft, ...normalizeClasses(props.leftClass) }">
                    <slot name="left" />
                </div>
                <div class="panel center flex-fill d-flex flex-column" :class="{ 'hasLeftSlot': hasLeftSlot, 'hasRightSlot': hasRightSlot, ...normalizeClasses(props.bodyClass) }">
                    <slot name="default" />
                </div>
                <div v-if="hasRightSlot" ref="panelRightRef" class="panel right d-flex flex-column" :class="{ 'show': showRight, ...normalizeClasses(props.rightClass) }" data-name="panel-right">
                    <div ref="panelRightContentRef" :style="{ 'top': `${offset.toFixed(2)}px` }">
                        <slot name="right" />
                    </div>
                </div>
            </div>
            <div v-if="hasFooter" class="card-footer" :class="{ 'card-footer-sticky': props.stickyFooter }">
                <PortalTarget name="data-card-footer" />
                <slot name="footer" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.toolbar {
    z-index: 10000;

    &.subcard {
        z-index: 9999;
    }
}
.card-form {
    --ideo-card-panel-width: 350px;

    margin: 0;

    &:not(.mobile) .sidebar-width {
        max-width: var(--ideo-card-panel-width);
    }

    .panel {
        &.left, &.right {
            max-width: var(--ideo-card-panel-width);
            min-width: var(--ideo-card-panel-width);
            width: var(--ideo-card-panel-width);
        }
        &.left {
            border-right: 1px solid var(--bs-border-color-translucent);
        }
        &.right {
            border-left: 1px solid var(--bs-border-color-translucent);

            & > div {
                position: relative;
            }
        }
        &.center {
            width: 100%;
        }
    }

    > .card-body {
        &:has(.left) > .center {
            width: calc(100% - var(--ideo-card-panel-width));
        }
        &:has(.right) .center {
            width: calc(100% - var(--ideo-card-panel-width));
        }
        &:has(.left):has(.right) .center {
            width: calc(100% - 2 * var(--ideo-card-panel-width));
        }
    }

    // Firefox fix - :has() is not supported in Firefox
     > .card-body {
        .center.hasLeftSlot, center.hasRightSlot {
            width: calc(100% - var(--ideo-card-panel-width));
        }
        .center.hasLeftSlot.hasRightSlot {
            width: calc(100% - 2 * var(--ideo-card-panel-width))
        }
    }

    > .card-footer-sticky {
        position: sticky;
        z-index: 200;
        left: 0;
        bottom: calc(var(--ideo-view-padding-size) + var(--bs-card-border-width));
        border-bottom: 1px solid var(--bs-border-color);
        background: var(--bs-tertiary-bg);

        &::after {
            content: '';
            position: absolute;
            left: -1px;
            top: calc(100% + 1px);
            background: var(--bs-tertiary-bg);
            width: calc(100% + 2px);
            height: calc(var(--ideo-view-padding-size) + var(--bs-card-border-width));
        }
    }

    &.mobile > .card-body .center {
        width: auto;
    }

    &.mobile.responsive {
        min-height: var(--ideo-view-mobile-height);

        .panel {
            transition: var(--ideo-transition);
            background-color: var(--bs-body-bg);

            &.left {
                margin-left: calc(-1 * var(--ideo-card-panel-width));

                &.show {
                    margin-left: 0;
                }
            }
            &.right {
                margin-right: calc(-1 * var(--ideo-card-panel-width));

                &.show {
                    margin-right: 0;
                }
            }
        }

        > .card-body {
            overflow-x: hidden;

            &:has(.left.show) > .center {
                margin-right: calc(-1 * var(--ideo-card-panel-width));
            }
            &:has(.right.show) .center {
                margin-left: calc(-1 * var(--ideo-card-panel-width));
            }
        }
    }

    &.mobile:not(.responsive) {
        .panel {
            padding: 1rem;

            &.left {
                border-right: none;
            }
            &.right {
                border-left: none;
            }
        }
    }

    &.tablet {
        --ideo-card-panel-width: 50%;
    }

    &.phone {
        --ideo-card-panel-width: 100%;

        .panel {
            border: none;
        }
    }
}
</style>
